<template>
  <b-modal ref="EditInvoiceItemModal"
           :no-close-on-backdrop="true" :no-enforce-focus="true" @hidden="clearInvoiceItemData">
    <div slot="modal-title">
      <div class="d-flex">
        <h5 class="modal-title">
          {{$t('modal_dialogs.edit_invoice_item')}}
        </h5>
      </div>
    </div>
    <div>
      <b-row v-if="isDebitOrCreditRelatedType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.invoice_item')}}</label>
        </b-col>
        <b-col cols="8">
          <multiselect v-model="invoiceItemData.invoice_item" :max-height="200"
                       placeholder="" label="name" track-by="name"
                       :options="invoiceItems" :multiple="false" :close-on-select="true"
                       :class="{required: !invoiceItemData.invoice_item}"
                       select-label="" deselect-label="" selected-label="" @select="setInvoiceItem"/>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="4">
          <label>{{$t('labels.invoice.name')}}</label>
        </b-col>
        <b-col cols="8">
          <input
              class="form-control"
              v-model="invoiceItemData.name"
              :class="{required: !invoiceItemData.name}">
        </b-col>
      </b-row>
      <b-row
          v-if="isCreditType || isDebitType || isDebitOrCreditRelatedType || isEquipmentType || isPartnerType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.shipped')}}</label>
        </b-col>
        <b-col cols="8" class="d-flex">
          <b-col cols="8" class="pl-0">
            <input class="form-control"
                   v-model.lazy="invoiceItemData.product_amount"
                   v-digits-after-decimal="{numberOfDigits: 3}"
                   :class="{required: !invoiceItemData.product_amount}"
                   @change="onAmountChange">
          </b-col>
          <b-col cols="4" class="pr-0">
            <multiselect v-model="invoiceItemData.measure_type" :max-height="200"
                         placeholder="" label="name" track-by="name"
                         select-label="" deselect-label="" selected-label=""
                         :options="measure_types_si" :close-on-select="true"
                         :class="{required: !invoiceItemData.measure_type}"
                         :disabled="(!isEquipmentType && !isPartnerType && !isDebitType && !isCreditType) || isDebitOrCreditRelatedType"/>
          </b-col>
        </b-col>
      </b-row>
      <b-row v-if="isEquipmentType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.net_cost_base_curr')}} {{invoiceItemCurrency}}/{{invoiceItemData.measure_type &&
            invoiceItemData.measure_type.key}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-model.lazy="invoiceItemData.cost_real_base_curr"
                 @change="onNetCostPerAmountChange"
                 v-digits-after-decimal="{numberOfDigits: 2}">
        </b-col>
      </b-row>
      <b-row v-if="isDebitOrCreditRelatedType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.invoice_item_price_base_curr')}} {{invoiceItemCurrency}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-model.lazy="invoiceItemData.cost_real"
                 @change="onCostRealChange"
                 v-digits-after-decimal="{numberOfDigits: 2}"
                 disabled>
        </b-col>
      </b-row>
      <b-row v-if="isEquipmentType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.net_cost_base_curr')}} {{invoiceItemCurrency}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-model.lazy="invoiceItemData.cost_real"
                 @change="onCostRealChange"
                 v-digits-after-decimal="{numberOfDigits: 2}">
        </b-col>
      </b-row>
      <b-row v-if="isCreditType || isCreditRelatedType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.discount_base_curr')}} {{invoiceItemCurrency}}/{{invoiceItemData.measure_type &&
            invoiceItemData.measure_type.key}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-replace-with-negative-and-digits-decimal="{numberOfDigits: 3}"
                 v-model.lazy="invoiceItemData.price"
                 @change="onPriceChange">
        </b-col>
      </b-row>
      <b-row v-if="isCreditRelatedType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.discount_proc')}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-replace-with-negative-and-digits-decimal="{numberOfDigits: 2}"
                 v-model.lazy="invoiceItemData.percent"
                 @change="onPercentChange">
        </b-col>
      </b-row>
      <b-row v-if="isDebitType || isDebitRelatedType || isPartnerType || isEquipmentType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.price_base_curr')}} {{invoiceItemCurrency}}/{{invoiceItemData.measure_type &&
            invoiceItemData.measure_type.key}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-digits-after-decimal="{numberOfDigits: 2}"
                 v-model.lazy="invoiceItemData.price"
                 @change="onPriceChange">
        </b-col>
      </b-row>
      <b-row v-if="isDebitRelatedType">
        <b-col cols="4">
          <label>{{$t('labels.invoice.price_proc')}}</label>
        </b-col>
        <b-col cols="8">
          <input class="form-control"
                 v-digits-after-decimal="{numberOfDigits: 2}"
                 v-model.lazy="invoiceItemData.percent"
                 @change="onPercentChange">
        </b-col>
      </b-row>
      <b-row
          v-if="isCreditType || isDebitType || isDebitOrCreditRelatedType || isCompensationType
        || isMarginCorrectType || isPartnerType || isEquipmentType">
        <b-col cols="4">
          <label>{{$t('tables.total')}}</label>
        </b-col>
        <b-col cols="8" v-if="isCreditType || isCreditRelatedType || isMarginCorrectType">
          <input class="form-control"
                 :class="{required: !invoiceItemData.cost_sale}"
                 v-replace-with-negative-and-digits-decimal="{numberOfDigits: 2}"
                 v-model.lazy="invoiceItemData.cost_sale"
                 @change="onTotalChange">
        </b-col>
        <b-col cols="8" v-else>
          <input class="form-control"
                 :class="{required: !invoiceItemData.cost_sale}"
                 v-digits-after-decimal="{numberOfDigits: 2}"
                 v-model.lazy="invoiceItemData.cost_sale"
                 @change="onTotalChange">
        </b-col>
      </b-row>
    </div>
    <div slot="modal-footer" class="w-100 d-flex justify-content-center align-items-center">
      <b-btn size="sm" class="ml-2" variant="success" @click="save"
             title="Save">
        {{$t('buttons.common.save')}}
      </b-btn>
      <b-btn size="sm" class="ml-2" variant="outline-success" @click="saveAndExit"
             title="Save & Exit">
        {{$t('buttons.common.save_and_exit')}}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
  import { mapState } from 'vuex';
  import { Multiselect } from 'vue-multiselect';
  import {
    COMMISSION,
    COMPENSATION,
    CREDIT,
    DEBIT,
    EQUIPMENT,
    INVOICE,
    MARGIN_CORRECT,
    PARTNER,
    SERVICE
  } from '@/constants/invoiceTypes';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { handlerResponse } from '@/helpers/commonHelpers';
  import FloatInput from '@/components/common/FloatInput';

  export default {
    props: ['invoiceTypeName', 'invoiceCurrency', 'invoiceLink', 'fetchPackagesCb', 'fetchInvoiceItemsCb', 'relatedInvoiceId', 'isInvoiceRelated', 'invoiceId'],
    components: {
      Multiselect,
      FloatInput
    },
    data() {
      return {
        invoiceItemDataInitial: {
          name: null,
          original_wood_item: null,
          cost_sale: 0,
          price: 0,
          percent: 0,
          type: null,
          price_per_measurement: 0,
          product_amount: 0,
          cost_real_base_curr: 0,
          invoice_item: null,
          measure_type: null
        },
        invoiceItemData: {},
        invoiceItems: [],
        isCreatingInvoiceItem: false
      }
    },
    computed: {
      ...mapState('woodware', ['measure_types_si']),
      isGeneralType() {
        return this.invoiceTypeName === INVOICE;
      },
      isPartnerType() {
        return this.invoiceTypeName === PARTNER;
      },
      isEquipmentType() {
        return this.invoiceTypeName === EQUIPMENT;
      },
      isCreditType() {
        return this.invoiceTypeName === CREDIT;
      },
      isDebitType() {
        return this.invoiceTypeName === DEBIT;
      },
      isServiceType() {
        return this.invoiceTypeName === SERVICE;
      },
      isCommissionType() {
        return this.invoiceTypeName === COMMISSION;
      },
      isCompensationType() {
        return this.invoiceTypeName === COMPENSATION;
      },
      isMarginCorrectType() {
        return this.invoiceTypeName === MARGIN_CORRECT;
      },
      isDebitRelatedType() {
        return this.isDebitType && this.isInvoiceRelated;
      },
      isCreditRelatedType() {
        return this.isCreditType && this.isInvoiceRelated;
      },
      isDebitOrCreditRelatedType() {
        return this.isDebitRelatedType || this.isCreditRelatedType;
      },
      invoiceItemCurrency() {
        return this.invoiceItemData.invoice_item && this.invoiceItemData.invoice_item.invoice_currency || this.invoiceCurrency.code;
      },
      prepareSaveData() {
        if (this.isGeneralType || this.isCommissionType || this.isServiceType) {
          return {
            name: this.invoiceItemData.name
          }
        }
        if (this.isMarginCorrectType || this.isCompensationType) {
          return {
            name: this.invoiceItemData.name,
            cost_sale: +this.invoiceItemData.cost_sale
          }
        }
        if ((this.isCreditType || this.isDebitType || this.isPartnerType) && !this.isInvoiceRelated) {
          return {
            name: this.invoiceItemData.name,
            product_amount: +this.invoiceItemData.product_amount,
            cost_sale: +this.invoiceItemData.cost_sale,
            measure_type: this.invoiceItemData.measure_type && this.invoiceItemData.measure_type.key,
            price: +this.invoiceItemData.price
          }
        }
        if (this.isCreatingInvoiceItem) {
          return {
            name: this.invoiceItemData.name,
            cost_sale: +this.invoiceItemData.cost_sale,
            product_amount: +this.invoiceItemData.product_amount,
            price: +this.invoiceItemData.price,
            cost_real: +this.invoiceItemData.cost_real,
            type: this.isEquipmentType ? 'equipment' : 'fictive',
            measure_type: this.invoiceItemData.measure_type && this.invoiceItemData.measure_type.key,
            original_wood_item: this.invoiceItemData.invoice_item && this.invoiceItemData.invoice_item.id
          }
        }
        return {
          name: this.invoiceItemData.name,
          cost_sale: +this.invoiceItemData.cost_sale,
          cost_real: +this.invoiceItemData.cost_real,
          product_amount: +this.invoiceItemData.product_amount,
          price: +this.invoiceItemData.price,
          measure_type: this.invoiceItemData.measure_type && this.invoiceItemData.measure_type.key,
          original_wood_item: this.invoiceItemData.invoice_item && this.invoiceItemData.invoice_item.id
        }
      }
    },
    methods: {
      async setDefaultInvoiceItemValues(invoiceItems) {
        if (invoiceItems.length) {
          this.invoiceItemData.invoice_item = invoiceItems[0];
          this.invoiceItemData.product_amount = invoiceItems[0].product_amount;
          this.invoiceItemData.cost_real = invoiceItems[0].full_cost_sale;
          this.invoiceItemData.measure_type = invoiceItems[0].measure_type;
          this.invoiceItemData.name = invoiceItems[0].name;
        }
      },
      async fetchRelatedInvoiceItems() {
        try {
          const params = {
            invoice: this.relatedInvoiceId,
            already_used_in_fictive_for_invoice: this.invoiceId
          };
          this.invoiceItems = await this.$http.get(`accounting/invoice_items_short_view/`, {params}).then(handlerResponse);
          await this.setDefaultInvoiceItemValues(this.invoiceItems);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      setInvoiceItem(invoiceItem) {
        this.invoiceItemData.product_amount = invoiceItem.product_amount;
        this.invoiceItemData.cost_real = invoiceItem.full_cost_sale;
        this.invoiceItemData.measure_type = invoiceItem.measure_type;
        this.invoiceItemData.name = invoiceItem.name;
      },
      async fetchInvoiceItems() {
        try {
          const params = {
            id: this.invoiceItemData.original_wood_item.id
          };
          this.invoiceItems = await this.$http.get(`accounting/invoice_items_short_view/`, {params}).then(handlerResponse);
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async fetchInvoiceItem(id) {
        try {
          const invoiceItem = await this.$http.get(`${this.invoiceLink}invoice_items/${id}/`).then(handlerResponse);
          this.invoiceItemData = {
            ...this.invoiceItemData,
            invoice_item_name: invoiceItem.name,
            ...invoiceItem
          };
          if (this.isDebitOrCreditRelatedType) {
            this.invoiceItemData.invoice_item = invoiceItem.original_wood_item;
          }
          if (this.isEquipmentType) {
            this.invoiceItemData.cost_real_base_curr = (+this.invoiceItemData.cost_real / +this.invoiceItemData.product_amount)
              .toFixed(2);
          }
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async save() {
        const data = this.prepareSaveData;
        try {
          if (!this.isCreatingInvoiceItem) {
            const invoiceData = await this.$http.put(`${this.invoiceLink}invoice_items/${this.invoiceItemData.id}/`, data).then(handlerResponse);
            this.invoiceItemData = {
              invoice_item: invoiceData.original_wood_item,
              ...invoiceData
            }
          } else {
            const invoiceData = await this.$http.post(`${this.invoiceLink}invoice_items/`, data).then(handlerResponse);
            this.invoiceItemData = {
              invoice_item: invoiceData.original_wood_item,
              ...invoiceData
            };
            this.isCreatingInvoiceItem = false;
          }
          if (this.isEquipmentType) {
            if (!this.invoiceItemData.product_amount) {
              this.invoiceItemData.cost_real_base_curr = 0;
            } else {
              this.invoiceItemData.cost_real_base_curr = (+this.invoiceItemData.cost_real / +this.invoiceItemData.product_amount).toFixed(2);
            }
          }
          if (this.isCreditRelatedType) {
            if (!this.invoiceItemData.cost_real) {
              this.invoiceItemData.percent = 0;
            } else {
              this.invoiceItemData.percent = (+this.invoiceItemData.cost_sale / +this.invoiceItemData.cost_real * 100).toFixed(2);
            }
          }
          if (this.isGeneralType || this.isCommissionType) {
            this.fetchPackagesCb();
          } else {
            this.fetchInvoiceItemsCb();
          }
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      async saveAndExit() {
        await this.save();
        this.$refs['EditInvoiceItemModal'].hide();
      },
      onTotalChange() {
        if (Number(this.invoiceItemData.cost_sale)) {
          if (+this.invoiceItemData.product_amount > 0) {
            this.invoiceItemData.price = (+this.invoiceItemData.cost_sale / +this.invoiceItemData.product_amount).toFixed(2);
          }
          if (+this.invoiceItemData.cost_real > 0) {
            this.invoiceItemData.percent = (+this.invoiceItemData.cost_sale / +this.invoiceItemData.cost_real * 100).toFixed(2);
          }
        }
      },
      onPriceChange() {
        if (Number(this.invoiceItemData.price)) {
          this.invoiceItemData.cost_sale = (+this.invoiceItemData.product_amount * +this.invoiceItemData.price).toFixed(2);
          if (+this.invoiceItemData.cost_real > 0) {
            this.invoiceItemData.percent = (+this.invoiceItemData.cost_sale / +this.invoiceItemData.cost_real * 100).toFixed(2);
          }
        }
      },
      onPercentChange() {
        if (Number(this.invoiceItemData.percent)) {
          this.invoiceItemData.cost_sale = (+this.invoiceItemData.cost_real * +this.invoiceItemData.percent / 100).toFixed(2);
          if (+this.invoiceItemData.product_amount > 0) {
            this.invoiceItemData.price = (+this.invoiceItemData.cost_sale / +this.invoiceItemData.product_amount).toFixed(2);
          }
        }
      },
      onAmountChange() {
        if (Number(this.invoiceItemData.product_amount)) {
          this.invoiceItemData.cost_sale = (+this.invoiceItemData.product_amount * +this.invoiceItemData.price).toFixed(2);
        }
      },
      onCostRealChange() {
        if (Number(this.invoiceItemData.cost_real)) {
          this.invoiceItemData.cost_sale = (+this.invoiceItemData.cost_real * +this.invoiceItemData.percent / 100).toFixed(2);
          if (+this.invoiceItemData.product_amount > 0) {
            this.invoiceItemData.cost_real_base_curr = (+this.invoiceItemData.cost_real / +this.invoiceItemData.product_amount).toFixed(2);
          }
        }
      },
      onNetCostPerAmountChange() {
        if (Number(this.invoiceItemData.cost_real_base_curr)) {
          this.invoiceItemData.cost_real = (+this.invoiceItemData.product_amount * +this.invoiceItemData.cost_real_base_curr).toFixed(2);
        }
      },
      clearInvoiceItemData() {
        this.invoiceItemData = {...this.invoiceItemDataInitial};
      }
    },
    mounted() {
      this.$root.$on('openEditInvoiceItemModal', async invoiceItem => {
        if (invoiceItem) {
          this.invoiceItemData = {
            ...this.invoiceItemDataInitial,
            percent: invoiceItem.invoice_correction_percent,
            ...invoiceItem
          };
          if (!this.isGeneralType && !this.isCommissionType) {
            await this.fetchInvoiceItem(invoiceItem.id);
          } else {
            await this.fetchInvoiceItem(invoiceItem.invoice_item_id);
          }
          this.isCreatingInvoiceItem = false;
        } else {
          this.invoiceItemData = {...this.invoiceItemDataInitial};
          this.isCreatingInvoiceItem = true;
          if (this.isInvoiceRelated && !this.isMarginCorrectType && !this.isCompensationType) {
            this.fetchRelatedInvoiceItems();
          }

        }
        if (this.$refs['EditInvoiceItemModal']) {
          this.$refs['EditInvoiceItemModal'].show();
        }
      });
    },
    beforeDestroy() {
      this.$root.$off('openEditInvoiceItemModal');
    }
  }
</script>

<style scoped>
  .modal-title {
    text-transform: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/modals/EditInvoiceItemModal.vue